
/**
 * BoCapital Auth Service — TypeScript port of auth.js
 * Token management, login/logout, 401 handling.
 */

import { resolveApiBase } from '../config/runtime';
import { resolveCapabilities } from '../config/authorization';

const TOKEN_KEY = 'bocapital_token';
const USERNAME_KEY = 'bocapital_username';
const ROLE_KEY = 'bocapital_role';
const LOGIN_TIMEOUT_MS = 30000;

function isCapacitor(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'capacitor:';
}

class AuthService {
  private _unauthorizedPending = false;

  /** Read token from storage */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  }

  /** Read username */
  getUsername(): string | null {
    return localStorage.getItem(USERNAME_KEY) || sessionStorage.getItem(USERNAME_KEY);
  }

  /**
   * Read the CACHED role.
   *
   * This is a cache of what the server last told us, not a source of truth —
   * localStorage is writable by anything running on the origin, including the
   * user. `revalidateSession()` re-derives it from the token on load and
   * corrects it. Treat this as "probably right, good enough to paint the first
   * frame", never as an authorization decision on its own.
   */
  getRole(): string | null {
    return localStorage.getItem(ROLE_KEY) || sessionStorage.getItem(ROLE_KEY);
  }

  /** Resolve current capability set from the cached role. See `getRole`. */
  getCapabilities(): string[] {
    return Array.from(resolveCapabilities(this.getRole()));
  }

  /**
   * Re-derive role + capabilities from the SERVER, using the bearer token as
   * the only input, and correct the local cache if it disagrees.
   *
   * Why: the UI used to gate nav and controls purely off the cached role, so
   * `localStorage.setItem('bocapital_role','admin')` unlocked the operator
   * console. The backend was never fooled — it reads the role from the token
   * claim — but not every mutating route carries a capability gate yet, and on
   * those the UI gate is the only one there is.
   *
   * Failure posture: a network error leaves the cache alone and reports no
   * change. We do NOT clear credentials on a failed probe — a transient blip
   * must not log the operator out mid-session. A genuine 401 is handled by the
   * normal `onUnauthorized` path.
   *
   * Returns the authoritative role when it could be established, else null.
   */
  async revalidateSession(): Promise<{ role: string; capabilities: string[] } | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const res = await fetch(`${resolveApiBase()}/api/auth/session`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) return null;

      const data = await res.json();
      const serverRole = String(data?.role ?? '').trim();
      if (!serverRole) return null;

      const cached = String(this.getRole() ?? '').trim().toLowerCase();
      if (cached !== serverRole.toLowerCase()) {
        console.warn(
          '[auth] cached role %o disagrees with the server (%o) — using the server value.',
          cached, serverRole,
        );
        // Rewrite into whichever store already holds the token, so we don't
        // silently promote a session-scoped login to a persistent one.
        const remember = localStorage.getItem(TOKEN_KEY) !== null;
        this.setRole(serverRole, remember);
      }

      const capabilities = Array.isArray(data?.capabilities)
        ? data.capabilities.map((c: unknown) => String(c))
        : Array.from(resolveCapabilities(serverRole));

      return { role: serverRole, capabilities };
    } catch (err) {
      console.warn('[auth] session revalidation failed; keeping cached role', err);
      return null;
    }
  }

  /** Store token after successful login */
  setToken(token: string, remember = false): void {
    // On native (Capacitor) builds, localStorage is the most reliable persistence
    // mechanism and survives app restarts. Treat the app as always "remembered"
    // on mobile so the user does not have to re-login each time the app restarts.
    const useLocalStorage = remember || isCapacitor();
    const storage = useLocalStorage ? localStorage : sessionStorage;
    storage.setItem(TOKEN_KEY, token);
  }

  /** Store username */
  setUsername(name: string, remember = false): void {
    const useLocalStorage = remember || isCapacitor();
    const storage = useLocalStorage ? localStorage : sessionStorage;
    storage.setItem(USERNAME_KEY, name);
  }

  /** Store role */
  setRole(role: string, remember = false): void {
    const useLocalStorage = remember || isCapacitor();
    const storage = useLocalStorage ? localStorage : sessionStorage;
    storage.setItem(ROLE_KEY, role);
  }

  /** Is the user authenticated? */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /** Handle 401 — clear credentials and redirect (debounced) */
  onUnauthorized(): void {
    // Debounce: only fire once per tick to avoid cascade from parallel API calls
    if (this._unauthorizedPending) return;
    this._unauthorizedPending = true;
    this.clearCredentials();
    queueMicrotask(() => {
      this._unauthorizedPending = false;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('bocapital:unauthorized'));
      }
    });
  }

  /** Login (POST /api/login) */
  async login(username: string, password: string, remember = false): Promise<{ success: boolean; error?: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);
    try {
      const apiBase = resolveApiBase();
      if (!apiBase) {
        return { success: false, error: 'API base is not configured. Set BOC_API_BASE or window.BOC_API_BASE.' };
      }
      const res = await fetch(`${apiBase}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        signal: controller.signal,
      });

      let data: any = {};
      try {
        data = await res.json();
      } catch {
        // Some edge failures can return non-JSON payloads; keep a safe fallback.
        data = {};
      }

      if (res.ok && data.token) {
        this.setToken(data.token, remember);
        const resolvedUsername = data.username || data.user?.username || username;
        const resolvedRole = data.role || data.user?.role || 'viewer';
        this.setUsername(resolvedUsername, remember);
        this.setRole(resolvedRole, remember);
        // Let the realtime socket reconnect immediately with the new token
        // (the WS authenticates via subprotocol, not the session cookie).
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('bocapital:login'));
        }
        return { success: true };
      }
      return { success: false, error: data.error || 'Login failed' };
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        return { success: false, error: 'Login request timed out. Check API reachability and try again.' };
      }
      return { success: false, error: err?.message || 'Network error' };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /** Logout — clear and redirect */
  logout(): void {
    this.clearCredentials();
    window.dispatchEvent(new CustomEvent('bocapital:logout'));
  }

  private clearCredentials(): void {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
    sessionStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(ROLE_KEY);
  }
}

export const authService = new AuthService();
export default authService;
